/**
 * @sidebet/link — drop-in round-up enrollment.
 *
 *   <script src="https://api-production-610a.up.railway.app/sdk/sidebet.js"></script>
 *   const link = SideBet.create({ linkToken, onSuccess(r) {…}, onExit() {…} });
 *   link.open();
 *
 * The link token comes from YOUR server: POST /api/roundup/link-token with your merchant API key.
 * Nothing secret ever reaches the browser. Copy, colors and the money choices a user sees come
 * from the brand's Link preset (configured in the SideBet admin).
 */

declare const __SDK_VERSION__: string;

export interface LinkConfig {
  /** From your server: POST /api/roundup/link-token */
  linkToken: string;
  /** API base. Defaults to the host the script was loaded from. */
  apiBase?: string;
  /** Called when the consumer has a verified bank and round-ups are on. */
  onSuccess?: (result: LinkSuccess) => void;
  /** Called when the consumer closes the flow without finishing. */
  onExit?: (reason: 'closed' | 'error' | 'expired', error?: Error) => void;
  /** Lifecycle events for analytics. */
  onEvent?: (event: LinkEvent) => void;
  /** Override the preset's display name (rarely needed). */
  merchantName?: string;
  /** Override preset theme tokens. */
  theme?: Partial<Theme>;
  /** Show the "how it works" step before choices/bank (default true). */
  explainer?: boolean;
  /** Open directly on the settings step (same as SideBet.settings()). */
  startAt?: 'consent' | 'choose' | 'bank';
  /** Admin preview: render against the draft preset; no bank link is created. */
  previewDraft?: boolean;
}

export interface Theme {
  accent: string;
  accentInk: string;
  bg: string;
  text: string;
  muted: string;
  line: string;
  well: string;
  radius: string;
  font: string;
}

export interface Accrual { accruedCents: number; thresholdCents: number; remainingCents: number; pct: number; count: number }
export interface Rules { strategy: 'CENTS' | 'NEAREST_DOLLAR'; multiplier: number; thresholdCents: number; destination: string; paused: boolean; perTransactionCeilingCents: number; dailyCapCents: number; weeklyCapCents: number }
export interface Settings { thresholdCents?: number | null; strategy?: 'CENTS' | 'NEAREST_DOLLAR' | null; multiplier?: number; destination?: string | null; paused?: boolean }
/** One gear, used by the widget's settings control. */
const GEAR = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" '
  + 'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/>'
  + '<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 '
  + '1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 '
  + '1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 '
  + '4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 '
  + '0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 '
  + '1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>';

export interface Bank { bankName: string | null; bankLast4: string | null; status: string; bankLogoUrl?: string | null }

export interface LinkSuccess {
  bank: Bank | null;
  accrual: Accrual;
  rules: Rules;
  wallet: { balanceCents: number; availableCents: number };
}

export type LinkEvent =
  | { type: 'open' }
  | { type: 'view'; step: Step }
  | { type: 'settings_saved'; settings: Settings }
  | { type: 'bank_link_started' }
  | { type: 'bank_linked'; status: string }
  | { type: 'success' }
  | { type: 'exit'; reason: string }
  | { type: 'error'; message: string };

type Step = 'loading' | 'consent' | 'choose' | 'bank' | 'details' | 'mfa' | 'pending' | 'success' | 'error';

export interface StatusResult {
  bankLinked: boolean;
  bank: Bank | null;
  accrual: Accrual;
  wallet: { balanceCents: number; availableCents: number };
  debits: { id: string; amountCents: number; status: string; transactionCount: number; createdAt?: string; updatedAt?: string }[];
}

/**
 * Turn provider/server error strings into something a person can act on. `channel` names where the
 * code was actually sent, so the recovery hint points at the right inbox.
 */
function friendly(err: Error, channel?: 'sms' | 'email'): string {
  let m = String(err?.message || 'Something went wrong');
  m = m.replace(/^[A-Za-z]+ (?:POST|GET|PUT|DELETE) \S+ failed:\s*/i, '').replace(/\s*\([A-Z]{0,3}\d{2,5}\)\s*$/, '');
  if (/invalid verification code|verification code.*(invalid|incorrect|wrong)|code (is )?(invalid|incorrect)/i.test(m)) {
    const sent = channel === 'sms' ? 'we texted you' : channel === 'email' ? 'we emailed you' : 'we sent you';
    return `That code didn't match. Check the 6-digit code ${sent} and try again.`;
  }
  if (/code/i.test(m) && /expired/i.test(m)) return 'That code has expired. Go back and request a new one.';
  // The API answers an aged-out link token with the bare code `link_token_expired`, which the
  // progress widget was printing at the user verbatim.
  if (/link_token_expired|link token (has )?expired/i.test(m)) return 'Your session expired. Reopen round-ups to continue.';
  if (/link_token_invalid|invalid link token|missing or invalid authorization/i.test(m)) return 'Round-ups are unavailable right now. Reopen the page to try again.';
  if (/numeric code/i.test(m)) return 'Enter the 6-digit code from the text message.';
  return m;
}

const reducedMotion = () => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
/** "today" / "yesterday" / "Sep 2" */
function when(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso); const now = new Date();
  const day = (x: Date) => Math.floor((x.getTime() - x.getTimezoneOffset() * 60000) / 86400000);
  const diff = day(now) - day(d);
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
/** Animate a money figure from → to over ~500ms (tabular-nums keeps the width stable). */
function countUp(el: HTMLElement, from: number, to: number, suffix = '') {
  if (reducedMotion() || from === to || Math.abs(to - from) > 100000) { el.textContent = money(to) + suffix; return; }
  const t0 = performance.now(); const dur = 500;
  const tick = (t: number) => {
    const p = Math.min(1, (t - t0) / dur); const e = 1 - Math.pow(1 - p, 3);
    el.textContent = money(Math.round(from + (to - from) * e)) + suffix;
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

interface Session extends StatusResult {
  merchant: { id: string; name: string };
  consumer: { id: string; email: string; firstName?: string; lastName?: string };
  rail?: { railMode: string; pspType: string };
  rules: Rules;
  settings: Settings | null;
  preset: any;
  copy: { headline: string; subhead: string; bullets: { title: string; detail: string }[]; entryNoun?: string; entryNounPlural?: string; destinationNoun?: string; branding: any };
  publishableKey: string | null;
  requiredFields?: string[];
  preview?: boolean;
}

// Stripe Link's palette: white card, navy text, quiet greys, one black button.
const LIGHT: Theme = { accent: '#1A1F36', accentInk: '#FFFFFF', bg: '#FFFFFF', text: '#1A1F36', muted: '#697386', line: '#E3E8EE', well: '#F6F8FA', radius: '16px', font: '-apple-system, "Helvetica Neue", "Segoe UI", Roboto, system-ui, sans-serif' };
const DARK: Partial<Theme> = { bg: '#1A1F36', text: '#F6F8FA', muted: '#A3ACB9', line: '#2E3650', well: '#232A44', accent: '#F6F8FA', accentInk: '#1A1F36' };

const money = (c: number) => (c < 0 ? '-' : '') + '$' + (Math.abs(c) / 100).toFixed(2);
const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]!);

/** WCAG contrast of white text on `hex`; below 4.5 we fall back to the theme's default button. */
function contrastOK(hex: string): boolean {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return false;
  const c = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  return (1.05 / (L + 0.05)) >= 4.5;
}

function scriptOrigin(): string {
  const s = (document.currentScript as HTMLScriptElement | null)?.src;
  if (s) return new URL(s).origin;
  const tag = Array.from(document.scripts).find((x) => /\/sdk\/sidebet(\.esm)?\.js/.test(x.src));
  return tag ? new URL(tag.src).origin : '';
}
const DEFAULT_API = scriptOrigin();

let stripeLoader: Promise<any> | null = null;
function loadStripe(pk: string): Promise<any> {
  const w = window as any;
  if (w.Stripe) return Promise.resolve(w.Stripe(pk));
  if (!stripeLoader) {
    stripeLoader = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://js.stripe.com/v3/';
      s.onload = () => resolve(null);
      s.onerror = () => reject(new Error('Could not load Stripe.js'));
      document.head.appendChild(s);
    });
  }
  return stripeLoader.then(() => w.Stripe(pk));
}

const AEROSYNC_CDN = 'https://cdn.sync.aero.inc/2.1.5/aerosync-widget.js';
let aerosyncLoader: Promise<any> | null = null;
function loadAerosync(): Promise<any> {
  const w = window as any;
  if (w.aerosync) return Promise.resolve(w.aerosync);
  if (!aerosyncLoader) {
    aerosyncLoader = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = AEROSYNC_CDN;
      s.onload = () => resolve(null);
      s.onerror = () => reject(new Error('Could not load the Aerosync widget'));
      document.head.appendChild(s);
    });
  }
  return aerosyncLoader.then(() => w.aerosync);
}

class Api {
  private base: string;
  constructor(base: string, private token: string) {
    this.base = base.replace(/\/+$/, '');
  }
  async call<T>(path: string, body?: unknown, method?: string): Promise<T> {
    const res = await fetch(this.base + path, {
      method: method || (body ? 'POST' : 'GET'),
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + this.token },
      body: body ? JSON.stringify(body) : undefined,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(json.message || (Array.isArray(json.details) ? json.details.join('; ') : json.error) || 'HTTP ' + res.status);
      (err as any).code = json.error;
      (err as any).status = res.status;
      throw err;
    }
    return json as T;
  }
}

/** Read-only status for a consumer (used by the widget or your own UI). */
export async function status(opts: { linkToken: string; apiBase?: string }): Promise<StatusResult> {
  return new Api(opts.apiBase || DEFAULT_API, opts.linkToken).call<StatusResult>('/api/link/status');
}

export interface LinkHandler { open(): void; close(): void; destroy(): void }

/**
 * One Link dialog at a time. Tab can leave a cross-origin bank frame and land on the merchant's own
 * page, so without this a stray Enter on the button behind opened a second modal on top of the first.
 */
let openInstance: symbol | null = null;

/**
 * Type the character ourselves when the host page cancelled the keystroke.
 *
 * Single-letter shortcuts on the embedding page (Vercel's toolbar uses "c" for comment; shopping
 * extensions do the same) call preventDefault on keydown, which stops the browser inserting the
 * character. They skip that only when they believe you are typing in a field — and our inputs sit
 * in a shadow root, so the element they inspect is our host <div>, not the input. The field then
 * silently refuses that letter: you cannot type Jackson, Marcus or Chase.
 *
 * Two approaches do NOT work. Stopping propagation at the window loses the race, because extension
 * content scripts register before any page script. Worse, it also stops the event reaching our own
 * input, which disables the repair below — that mistake is what made the first fix a no-op.
 *
 * So we let the event propagate normally and repair the damage at the input: a printable keystroke
 * that arrives already cancelled gets inserted by hand.
 */
function keepTypingThroughHostShortcuts(input: HTMLInputElement): void {
  input.addEventListener('keydown', (e: KeyboardEvent) => {
    if (!e.defaultPrevented) return;                 // nothing swallowed it; the browser will type
    if (e.ctrlKey || e.metaKey || e.altKey) return;  // a genuine shortcut, leave it alone
    if (e.key.length !== 1) return;                  // not a printable character
    if (input.maxLength >= 0 && input.value.length >= input.maxLength) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    input.value = input.value.slice(0, start) + e.key + input.value.slice(end);
    const caret = start + 1;
    input.setSelectionRange(caret, caret);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

/** Everything except our host is made inert while the dialog is open, so nothing behind it can be
 *  focused, clicked or read by a screen reader. */
function setBackgroundInert(host: HTMLElement, on: boolean): void {
  const kids = Array.from(document.body.children) as HTMLElement[];
  for (const el of kids) {
    if (el === host) continue;
    if (on) {
      if (!el.hasAttribute('inert')) { el.setAttribute('inert', ''); el.setAttribute('data-sb-inert', ''); }
    } else if (el.hasAttribute('data-sb-inert')) {
      el.removeAttribute('inert');
      el.removeAttribute('data-sb-inert');
    }
  }
}

export function create(config: LinkConfig): LinkHandler {
  const apiBase = (config.apiBase || DEFAULT_API).replace(/\/+$/, '');
  const api = new Api(apiBase, config.linkToken);
  const emit = (e: LinkEvent) => { try { config.onEvent?.(e); } catch { /* consumer code */ } };

  let host: HTMLDivElement | null = null;
  let root: ShadowRoot | null = null;
  let session: Session | null = null;
  let theme: Theme = LIGHT;
  let step: Step = 'loading';
  let lastFocus: Element | null = null;
  // choices the user is making on the "choose" step
  let choice: Settings = {};
  // The consent screen is the only way to reach a first-time bank link, whatever the entry point.
  let agreedAt: string | null = null;
  let linking = false;
  let msgListener: ((e: MessageEvent) => void) | null = null;
  let prevOverflow: string | null = null;
  const instanceId = Symbol('sidebet-link');

  function buildTheme(): Theme {
    const dark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    let t: Theme = { ...LIGHT, ...(dark ? DARK : {}) };
    const accent = session?.preset?.branding?.accentColor;
    if (accent && contrastOK(accent)) t = { ...t, accent, accentInk: '#FFFFFF' };
    return { ...t, ...(config.theme || {}) };
  }

  const css = () => `
    :host{all:initial}
    .ov{position:fixed;inset:0;background:rgba(26,31,54,.55);display:flex;align-items:center;justify-content:center;z-index:2147483000;font-family:${theme.font};color:${theme.text};padding:16px;box-sizing:border-box;-webkit-font-smoothing:antialiased}
    .m{background:${theme.bg};border-radius:${theme.radius};width:100%;max-width:400px;padding:24px;box-sizing:border-box;box-shadow:0 24px 64px rgba(26,31,54,.24);position:relative;animation:in .18s ease-out;max-height:calc(100vh - 32px);overflow:auto}
    @keyframes in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
    .m:focus{outline:0}
    .st{animation:fade .16s ease-out}
    @keyframes fade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
    @media (prefers-reduced-motion:reduce){.m,.st,.ex{animation:none!important}.chip,.seg button,.bar div{transition:none!important}}
    form{margin:0}
    .pill{display:inline-block;font-size:12px;font-weight:600;border-radius:999px;padding:3px 10px;background:${theme.well};color:${theme.muted}}
    .pill.on{background:#E3F5EE;color:#0E7A55}
    .pill.off{background:#FFF1E7;color:#B54708}
    @media (max-width:480px){.ov{align-items:flex-end;padding:0}.m{max-width:none;border-radius:20px 20px 0 0;padding:14px 20px calc(20px + env(safe-area-inset-bottom));max-height:92vh}.m:before{content:"";display:block;width:36px;height:4px;border-radius:2px;background:${theme.line};margin:0 auto 14px}}
    .x{position:absolute;top:10px;right:10px;width:44px;height:44px;border-radius:50%;border:0;background:${theme.well};color:${theme.muted};font-size:20px;cursor:pointer;line-height:1;transition:transform .12s}
    .x:active{transform:scale(.94)}
    .x:focus-visible,.b:focus-visible,.chip:focus-visible,.seg button:focus-visible{outline:2px solid ${theme.accent};outline-offset:2px}
    .lock{display:flex;justify-content:center;align-items:center;gap:10px;margin:4px 0 18px}
    .lock i{width:48px;height:48px;border-radius:12px;background:${theme.well};border:1px solid ${theme.line};display:flex;align-items:center;justify-content:center;font-weight:600;font-size:14px;overflow:hidden;font-style:normal}
    .lock i img{width:100%;height:100%;object-fit:cover}
    .lock i.rail{background:#fff;border-color:${theme.line}}
    .lock i.rail svg{display:block}
    .lock i.rail img{width:auto;height:auto;max-width:62%;max-height:62%;object-fit:contain}
    .acct-row{display:flex;align-items:center;gap:10px;padding:10px;border:1px solid ${theme.line};
      border-radius:12px;margin-bottom:8px}
    .acct-row.on{border-color:${theme.accent}}
    .acct-row i{flex:none;width:34px;height:34px;border-radius:9px;background:${theme.well};display:flex;
      align-items:center;justify-content:center;font-style:normal;font-weight:700;font-size:12px;overflow:hidden}
    .acct-row i.il{background:#fff;border:1px solid ${theme.line};padding:4px}
    .acct-row i.il img{max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;display:block}
    .acct-nm{flex:1;min-width:0}
    .acct-nm b{display:block;font-size:14px;font-weight:600}
    .acct-nm small{display:block;font-size:12px;color:${theme.muted}}
    .badge{font-style:normal;font-size:11px;font-weight:700;letter-spacing:.04em;color:${theme.accent};
      border:1px solid ${theme.accent};border-radius:99px;padding:3px 8px}
    .acct-btn{border:1px solid ${theme.line};background:transparent;color:${theme.text};border-radius:99px;
      padding:5px 12px;font:600 12px/1 inherit;cursor:pointer}
    .acct-btn:hover{border-color:${theme.accent};color:${theme.accent}}
    .acct-x{border:0;background:transparent;color:${theme.muted};font-size:19px;line-height:1;cursor:pointer;
      padding:2px 4px;border-radius:6px}
    .acct-x:hover{color:#B42318}
    .acct-note{margin:0 0 8px;font-size:13px;color:${theme.muted};text-align:left}
    .acct-note.warn{color:#B42318}
    .addbank{width:100%;border:1px dashed ${theme.line};background:transparent;color:${theme.text};
      border-radius:12px;padding:11px;font:600 13px/1 inherit;cursor:pointer;margin-bottom:4px}
    .addbank:hover{border-color:${theme.accent};color:${theme.accent}}
    .lock s{width:22px;height:2px;background:${theme.line};text-decoration:none}
    h1{font-size:20px;line-height:1.25;margin:0 0 6px;font-weight:600;text-align:center;text-wrap:balance;letter-spacing:-.01em}
    p.sub{font-size:14px;line-height:1.45;color:${theme.muted};text-align:center;margin:0 0 16px}
    .row{display:flex;gap:12px;align-items:flex-start;margin:12px 0}
    .row i{flex:none;width:40px;height:40px;border-radius:50%;background:${theme.well};display:flex;align-items:center;justify-content:center;font-style:normal;font-size:16px;color:${theme.text}}
    .row i.bl{background:#fff;overflow:hidden;padding:6px}
    .row i.bl img{max-width:100%;max-height:100%;object-fit:contain;display:block}
    .row b{display:block;font-size:14px;font-weight:500}
    .row span{font-size:13px;color:${theme.muted};line-height:1.4}
    .lbl{display:block;font-size:13px;font-weight:500;margin:16px 0 8px}
    .chips{display:flex;gap:8px;flex-wrap:wrap}
    .chip{border:1px solid ${theme.line};background:transparent;color:${theme.text};border-radius:999px;padding:10px 16px;font-size:14px;cursor:pointer;font-family:inherit;font-variant-numeric:tabular-nums;min-height:44px;transition:background .15s,color .15s,border-color .15s,transform .12s cubic-bezier(.2,.8,.2,1)}
    .chip:hover{border-color:${theme.muted}}
    .chip:active{transform:scale(.94)}
    .chip[aria-checked="true"]{background:${theme.accent};color:${theme.accentInk};border-color:${theme.accent}}
    .seg{display:flex;border:1px solid ${theme.line};border-radius:10px;overflow:hidden}
    .seg button{flex:1;border:0;background:transparent;color:${theme.muted};padding:10px;font-size:13px;cursor:pointer;font-family:inherit;min-height:44px;transition:background .15s,color .15s}
    .seg button:active{background:${theme.well}}
    .seg button[aria-checked="true"]{background:${theme.well};color:${theme.text};font-weight:500}
    .ex{background:${theme.well};border-radius:10px;padding:10px 12px;font-size:13px;color:${theme.muted};margin-top:12px;font-variant-numeric:tabular-nums;line-height:1.5}
    .ex b{color:${theme.text}}
    .ex.flash{animation:flash .35s ease-out}
    @keyframes flash{0%{background:${theme.line}}100%{background:${theme.well}}}
    .ex .est{display:block;font-size:12px;margin-top:2px}
    .b{display:block;width:100%;min-height:48px;padding:13px;border:0;border-radius:8px;background:${theme.accent};color:${theme.accentInk};font-weight:600;font-size:15px;cursor:pointer;margin-top:16px;font-family:inherit}
    .b:disabled{opacity:.5;cursor:default}
    .lnk{display:block;width:100%;background:none;border:0;color:${theme.muted};font-size:13px;margin-top:6px;cursor:pointer;font-family:inherit;text-align:center;min-height:44px;border-radius:8px}
    .lnk:hover{color:${theme.text}}
    .legal{font-size:12px;color:${theme.muted};line-height:1.45;margin-top:14px}
    .foot{font-size:11px;letter-spacing:.06em;color:${theme.muted};text-align:center;margin-top:14px;text-transform:uppercase}
    .bar{height:8px;border-radius:999px;background:${theme.well};overflow:hidden;margin:8px 0}
    .bar div{height:100%;background:${theme.accent};transition:width .5s cubic-bezier(.2,.8,.2,1)}
    .kv{display:flex;justify-content:space-between;font-size:13px;color:${theme.muted};font-variant-numeric:tabular-nums}
    .kv b{color:${theme.text}}
    .spin{width:28px;height:28px;border:3px solid ${theme.line};border-top-color:${theme.accent};border-radius:50%;animation:sp .8s linear infinite;margin:24px auto}
    @keyframes sp{to{transform:rotate(360deg)}}
    .ok{width:48px;height:48px;border-radius:50%;background:#E3F5EE;color:#0E9F6E;display:flex;align-items:center;justify-content:center;font-size:22px;margin:0 auto 12px}
    .err{color:#B42318;font-size:13px;margin-top:8px;line-height:1.4}
    .in{display:block;width:100%;box-sizing:border-box;border:1px solid ${theme.line};border-radius:8px;padding:12px;font-size:15px;font-family:inherit;background:${theme.bg};color:${theme.text};margin-top:8px;min-height:44px}
    .in:focus-visible{outline:2px solid ${theme.accent};outline-offset:1px}
    .mockbar{display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid ${theme.line};margin-bottom:4px}
    .mockbar b{font-size:13px;letter-spacing:.02em;display:flex;align-items:center;gap:6px}
    .mockbar b img{height:14px;width:auto;display:block}
    .mockh2{display:flex;align-items:center;gap:10px;margin:10px 0 2px}
    .mockh2 .mockh{margin:0}
    .bank i.il,.mockh2 i.il{background:#fff;border:1px solid ${theme.line};overflow:hidden;padding:4px;
      display:flex;align-items:center;justify-content:center}
    .bank i.il img,.mockh2 i.il img{max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;display:block}
    .mockh2 i.il{flex:none;width:34px;height:34px;border-radius:9px}
    .mockbar em{font-style:normal;font-size:10px;letter-spacing:.08em;color:#B45309;background:#FEF3C7;border-radius:99px;padding:3px 8px}
    .mockh{font-size:15px;margin:16px 0 4px}
    .banks{display:flex;flex-direction:column;gap:6px;max-height:290px;overflow:auto;margin-top:8px}
    .bank{display:flex;gap:10px;align-items:center;width:100%;text-align:left;padding:10px;margin:0;background:${theme.bg};color:${theme.text};border:1px solid ${theme.line};border-radius:10px;font:inherit;font-size:14px;font-weight:500;cursor:pointer;min-height:56px}
    .bank:hover:not(:disabled){border-color:${theme.muted}}
    .bank:disabled{opacity:.5;cursor:default}
    .bank:focus-visible{outline:2px solid ${theme.accent};outline-offset:2px}
    .bank i{flex:none;width:36px;height:36px;border-radius:8px;color:#fff;display:flex;align-items:center;justify-content:center;font-style:normal;font-weight:700;font-size:13px}
    .bank small{display:block;color:${theme.muted};font-weight:400;font-size:12px}
    .acct{margin-top:8px}
    .link{display:block;width:100%;margin-top:10px;padding:10px;border:0;background:none;color:${theme.muted};font:inherit;font-size:13px;cursor:pointer;min-height:44px}
    .link:focus-visible{outline:2px solid ${theme.accent};outline-offset:2px}
    .frame{display:block;border:0;width:100%;height:480px;border-radius:12px;background:${theme.well};margin-top:4px}
    .pv{position:absolute;top:14px;left:14px;font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:${theme.muted};background:${theme.well};border-radius:999px;padding:4px 8px}
  `;

  function mount() {
    if (host) return;
    lastFocus = document.activeElement;
    host = document.createElement('div');
    host.setAttribute('data-sidebet-link', '');
    root = host.attachShadow({ mode: 'open' });
    document.body.appendChild(host);
    // Keys typed inside the modal stay inside it: handled here, then stopped, so page-level hotkeys
    // (Vercel's toolbar, app shortcuts) never see keystrokes from our shadow-root inputs.
    host.addEventListener('keydown', onKey);
    for (const t of ['keydown', 'keyup', 'keypress'] as const) host.addEventListener(t, (e) => e.stopPropagation());
    document.addEventListener('keydown', onDocKey);
    // The page behind must not scroll, take focus, or be clickable while the dialog is open.
    prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    setBackgroundInert(host, true);
  }
  /** Escape still closes when focus has wandered to the page (e.g. after a backdrop click). */
  function onDocKey(e: KeyboardEvent) { if (e.key === 'Escape') exit('closed'); }
  const focusables = () => Array.from(root?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),a[href],iframe,[tabindex="0"]') || []).filter((el) => !el.hidden);
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') { exit('closed'); return; }
    // Keep Tab inside the dialog: the page behind is inert while we're open.
    if (e.key === 'Tab' && root) {
      const f = focusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      const active = (root as any).activeElement as HTMLElement | null;
      const inside = active && f.includes(active);
      if (!inside) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
      // Tab on the bank frame itself must enter the frame, not wrap around to the close button.
      if (active && active.tagName === 'IFRAME' && !e.shiftKey) return;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }
  }

  function render(html: string, opts: { noClose?: boolean } = {}) {
    if (!root) return;
    const brand = session?.preset?.branding;
    const foot = brand?.whiteLabel ? '' : '<div class="foot">Round-ups by Hedge</div>';
    const pv = session?.preview ? '<span class="pv">Preview</span>' : '';
    root.innerHTML = `<style>${css()}</style><div class="ov" part="overlay"><div class="m" role="dialog" aria-modal="true" aria-labelledby="sb-title" tabindex="-1">${pv}${opts.noClose ? '' : '<button class="x" aria-label="Close">×</button>'}<div class="st">${html}</div>${foot}</div></div>`;
    root.querySelector('.x')?.addEventListener('click', () => exit('closed'));
    // A stray backdrop click must not throw away a half-finished bank link; use × or Esc on those steps.
    root.querySelector('.ov')!.addEventListener('click', (e) => { if (e.target === e.currentTarget && !['bank', 'details', 'mfa'].includes(step)) exit('closed'); });
    // Errors are announced to screen readers; the "Please fill in…" style messages clear as soon as the user types.
    root.querySelectorAll<HTMLElement>('.err').forEach((el) => { el.setAttribute('role', 'alert'); el.setAttribute('aria-live', 'polite'); });
    root.querySelectorAll<HTMLInputElement>('input').forEach((inp) => {
      inp.addEventListener('input', () => { const el = root!.querySelector<HTMLElement>('.err'); if (el && !el.hidden) el.hidden = true; });
      keepTypingThroughHostShortcuts(inp);
    });
    // First field gets focus on form steps; the dialog itself otherwise.
    const first = root.querySelector<HTMLElement>('input.in');
    (first || (root.querySelector('.m') as HTMLElement)).focus();
    // A broken logo URL degrades to the initials tile instead of a broken-image icon.
    // Both lockup tiles (the brand's logo and the SideBet mark) degrade to their lettering.
    root.querySelectorAll<HTMLImageElement>('[data-logo] img').forEach((img) => {
      img.addEventListener('error', () => {
        const tile = img.parentElement!;
        tile.textContent = tile.querySelector('span')?.textContent || '';
      });
    });
  }

  function showErr(msg: string, focusId?: string) {
    const el = root?.querySelector<HTMLElement>('#err');
    if (el) { el.hidden = false; el.textContent = msg; }
    if (focusId) { const inp = root?.querySelector<HTMLInputElement>('#' + focusId); if (inp) { inp.focus(); inp.select?.(); } }
  }
  /** Wire a step's <form> so Enter submits, and the primary button shows a pending state. */
  function onSubmit(fn: () => void | Promise<void>) {
    const form = root!.querySelector<HTMLFormElement>('form');
    form?.addEventListener('submit', (e) => { e.preventDefault(); void fn(); });
  }

  function show(next: Step, html: string, opts?: { noClose?: boolean }) {
    step = next;
    emit({ type: 'view', step });
    render(html, opts);
  }

  const name = () => esc(config.merchantName || session?.preset?.branding?.displayName || session?.merchant?.name || 'this app');
  /** Human name of the PSP the consumer will link on (drives copy, nothing else). */
  const pspName = () => {
    const p = session?.rail?.pspType;
    return p === 'AEROPAY' ? 'Aeropay' : p === 'COINFLOW' ? 'CoinFlow' : 'Stripe';
  };
  /**
   * Who handles the bank sign-in screen. Aeropay's linking widget is branded Aerosync, but naming
   * both in consumer copy reads as two separate companies handling your bank — so consumers only
   * ever see the processor's name. "Aerosync" stays where it belongs: on the widget itself.
   */
  const bankHandler = () => pspName();
  const legalName = () => esc(session?.preset?.branding?.legalName || session?.preset?.branding?.displayName || session?.merchant?.name || 'the merchant');
  const initials = (s: string) => s.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  /** Funding accounts, loaded only when the manage screen is open. */
  interface FundingAccount { id: string; bankName: string | null; last4: string | null; status: string;
    accountType: string | null; logoUrl: string | null; active: boolean }
  let accountsCache: FundingAccount[] = [];
  /** loading / ok / stale (the PSP no longer knows this account) / error (the call failed). */
  let banksState: 'loading' | 'ok' | 'stale' | 'error' = 'loading';

  const addLabel = () => (banksState === 'stale' || banksState === 'error' ? 'Reconnect your bank' : '+ Add another bank');

  function banksNote(text: string, tone?: 'warn'): string {
    return `<p class="acct-note${tone ? ' warn' : ''}">${text}</p>`;
  }

  /** What the Banks list shows right now. Each state reads differently on purpose: an empty
   *  list and a failed load are not the same thing, and saying so is the difference between
   *  "add a bank" and "your bank needs reconnecting". */
  function banksBody(): string {
    if (banksState === 'loading') return banksNote('Loading your accounts…');
    if (banksState === 'error') return banksNote('We could not load your accounts just now.', 'warn');
    if (banksState === 'stale') return banksNote(`${esc(session?.bank?.bankName || 'Your bank')} needs reconnecting before round-ups can transfer.`, 'warn');
    if (!accountsCache.length) return banksNote('No bank is funding round-ups.');
    return accountsCache.map(accountRow).join('');
  }

  function accountRow(a: FundingAccount): string {
    const mark = a.logoUrl
      ? `<i class="il"><img alt="" src="${esc(a.logoUrl)}" decoding="async" onerror="this.remove()"></i>`
      : `<i>${esc(initials(a.bankName || 'Bank'))}</i>`;
    // The raw provider status ("verified", "pending_verification") is database language; say what
    // it means for the consumer instead.
    const s = String(a.status).toLowerCase();
    const sub = a.active ? 'Funding round-ups'
      : s === 'verified' ? 'Ready to use'
      : s.startsWith('pending') ? 'Still verifying'
      : esc(s.replace(/_/g, ' '));
    return `<div class="acct-row${a.active ? ' on' : ''}">
      ${mark}
      <span class="acct-nm"><b>${esc(a.bankName || 'Bank')}${a.last4 ? ` ••${esc(a.last4)}` : ''}</b><small>${sub}</small></span>
      ${a.active ? '<em class="badge">Active</em>' : `<button class="acct-btn" data-use="${esc(a.id)}" type="button">Use</button>`}
      <button class="acct-x" data-rm="${esc(a.id)}" type="button" aria-label="Remove ${esc(a.bankName || 'this bank')}">&times;</button>
    </div>`;
  }

  function lockup() {
    const b = session?.preset?.branding;
    const ini = esc(initials(b?.displayName || session?.merchant?.name || 'M'));
    const logo = b?.logoUrl ? `<img alt="" src="${esc(b.logoUrl)}" loading="eager" decoding="async"><span hidden>${ini}</span>` : ini;
    // The SideBet mark ships beside the bundle and is served from the same host, so it needs no
    // extra origin. If it fails to load the tile falls back to the "SB" lettering underneath.
    const mark = `<img alt="" src="${esc(apiBase)}/sdk/sidebet-mark.png" loading="eager" decoding="async"><span hidden>SB</span>`;
    const rail = b?.showRail ? `<s></s><i class="rail">${railMark()}</i>` : '';
    return `<div class="lock"><i data-logo>${logo}</i><s></s><i data-logo>${mark}</i>${rail}</div>`;
  }

  /**
   * The rail's tile. Aeropay ships beside the bundle like the SideBet mark, so it is the real
   * logo rather than a trace of it; every other rail falls back to its initials, which is honest
   * and needs no asset.
   */
  function railMark(): string {
    if (session?.rail?.pspType === 'AEROPAY') {
      return `<img alt="" src="${esc(apiBase)}/sdk/aeropay-mark.png" loading="eager" decoding="async">`;
    }
    return esc(initials(pspName()));
  }

  /** Effective threshold/rule/multiplier for the example line, from current choice + rules. */
  function eff() {
    const r = session!.rules;
    return {
      thresholdCents: choice.thresholdCents ?? r.thresholdCents,
      strategy: choice.strategy ?? r.strategy,
      multiplier: choice.multiplier ?? r.multiplier ?? 1,
    };
  }
  function example() {
    const e = eff();
    const raw = e.strategy === 'CENTS' ? 37 : 63;
    const cap = session!.rules.perTransactionCeilingCents;
    const cents = Math.min(raw * e.multiplier, cap);
    // Count in terms of the example on screen. Using a statistical average (~50¢) instead meant the
    // two halves of this line disagreed: "round up $0.37" beside "10 entries" to reach $5.00.
    const remaining = Math.max(0, e.thresholdCents - (session!.accrual?.accruedCents || 0));
    const n = Math.max(1, Math.ceil(remaining / Math.max(1, cents)));
    // Brands name this differently — a sportsbook has bets, daily fantasy has entries or lineups.
    const noun = esc(session!.copy?.entryNoun || 'bet');
    const plural = esc(session!.copy?.entryNounPlural || `${noun}s`);
    const verb = noun.charAt(0).toUpperCase() + noun.slice(1);
    return `${verb} <b>$4.37</b> → round up <b>${money(cents)}</b> · transfers at <b>${money(e.thresholdCents)}</b><span class="est">about ${n} ${n === 1 ? noun : plural} like that to your next transfer</span>`;
  }

  // ---------------------------------------------------------------- steps

  function consent() {
    const c = session!.copy;
    const icons = ['◎', '↗', '✕'];
    show('consent', `
      ${lockup()}
      <h1 id="sb-title">${esc(c.headline)}</h1>
      <p class="sub">${esc(c.subhead)}</p>
      ${c.bullets.map((b, i) => `<div class="row"><i>${icons[i] || '•'}</i><div><b>${esc(b.title)}</b><span>${esc(b.detail)}</span></div></div>`).join('')}
      <button class="b" id="go">Agree and continue</button>
      <button class="lnk" id="no">Not now</button>
      <div class="legal">${authorization()}</div>`);
    root!.querySelector('#go')!.addEventListener('click', () => { agreedAt = new Date().toISOString(); hasChoices() ? choose() : bank(); });
    root!.querySelector('#no')!.addEventListener('click', () => exit('closed'));
  }
  /** First-time links must pass through consent, no matter which entry point (widget "Change", settings(), startAt) got us here. */
  const needsConsent = () => !agreedAt && !session!.bankLinked && !session!.preview;

  /**
   * The debit authorization. It has to answer, in order: who takes the money, from where, how much,
   * how often, where it lands, and how to stop it. The previous wording named four companies in one
   * sentence, called an amount a maximum while saying it could be raised, and never said where the
   * money went — all things a partner's compliance reviewer picks up immediately.
   */
  function authorization(): string {
    const cap = money(eff().thresholdCents);
    const brand = name();
    // Name the destination *and* who holds it. "Where does my money actually go, and who has it?"
    // is the first question a partner's compliance team asks, and the first a user thinks. Which
    // wording applies follows the merchant's destination policy, so it can never drift from reality.
    const merchantWallet = session?.rules?.destination === 'MERCHANT_WALLET';
    const noun = esc(session?.copy?.destinationNoun || 'savings balance');
    const where = merchantWallet
      ? `your ${brand} ${noun}, which ${brand} holds for you`
      : `your own ${noun}, which ${brand} does not hold`;
    const processor = /hedge,? inc/i.test(legalName()) ? '' : `, through its payment provider Hedge, Inc.,`;
    return `By continuing you authorize ${legalName()}${processor} to debit your linked bank account `
      + `each time your round-ups reach ${cap}, and to move that money into ${where}. `
      + `Each transfer is for the amount you have rounded up, never more than ${cap} unless you raise it yourself. `
      + `You can change the amount, pause, or cancel at any time in ${brand}. `
      + `Bank sign-in is handled by ${bankHandler()}; Hedge never sees your credentials.`;
  }

  function hasChoices() {
    const p = session?.preset;
    return Boolean(p && (p.threshold.consumerCanChoose || p.rule.consumerCanChoose || p.destination.consumerCanChoose));
  }

  function choose(opts: { manage?: boolean } = {}) {
    const p = session!.preset;
    const e = eff();
    const parts: string[] = [];
    if (p.threshold.consumerCanChoose) {
      parts.push(`<div class="lbl">Transfer when I reach</div><div class="chips" role="radiogroup" aria-label="Transfer threshold">${(p.threshold.options as number[]).map((c) => `<button class="chip" role="radio" data-k="thresholdCents" data-v="${c}" aria-checked="${c === e.thresholdCents}">${money(c)}</button>`).join('')}</div>`);
    }
    if (p.rule.consumerCanChoose) {
      if (p.rule.options.length > 1) parts.push(`<div class="lbl">Round-up rule</div><div class="seg" role="radiogroup" aria-label="Round-up rule">${(p.rule.options as string[]).map((r) => `<button role="radio" data-k="strategy" data-v="${r}" aria-checked="${r === e.strategy}">${r === 'CENTS' ? 'Just the cents' : 'Up to the next dollar'}</button>`).join('')}</div>`);
      if (p.rule.multipliers.length > 1) parts.push(`<div class="lbl">Boost</div><div class="chips" role="radiogroup" aria-label="Multiplier">${(p.rule.multipliers as number[]).map((m) => `<button class="chip" role="radio" data-k="multiplier" data-v="${m}" aria-checked="${m === e.multiplier}">${m}×</button>`).join('')}</div>`);
    }
    if (p.destination.consumerCanChoose && p.destination.options.length > 1) {
      const cur = choice.destination ?? session!.rules.destination;
      parts.push(`<div class="lbl">Where it goes</div><div class="seg" role="radiogroup" aria-label="Destination">${(p.destination.options as string[]).map((d) => `<button role="radio" data-k="destination" data-v="${d}" aria-checked="${d === cur}">${esc((p.destination.labels?.[d] || d).replace(/\{merchant\}/g, session!.preset.branding.displayName))}</button>`).join('')}</div>`);
    }
    // Banks only belong on the manage screen: during enrollment there is nothing linked yet.
    const banksSection = opts.manage && session!.bankLinked
      ? `<div class="lbl">Banks</div><div id="accts">${banksBody()}</div>
         <button class="addbank" id="addbank" type="button">${addLabel()}</button>`
      : '';
    show('choose', `
      <h1 id="sb-title">${opts.manage ? 'Manage round-ups' : 'Choose your round-up'}</h1>
      <p class="sub">${opts.manage ? 'Change how much you round up and when it transfers.' : 'You can change this anytime.'}</p>
      ${banksSection}
      ${parts.join('')}
      <div class="ex" id="ex">${example()}</div>
      <button class="b" id="go">${opts.manage ? 'Save' : 'Continue'}</button>
      <button class="lnk" id="rec">${opts.manage ? (session!.rules.paused ? 'Resume round-ups' : 'Pause round-ups') : 'Use recommended'}</button>
      <div class="err" id="err" hidden></div>`);
    root!.querySelectorAll<HTMLButtonElement>('[role="radio"]').forEach((btn) => {
      btn.type = 'button';
      btn.addEventListener('click', () => {
        const k = btn.dataset.k as keyof Settings;
        const v = btn.dataset.v!;
        (choice as any)[k] = k === 'thresholdCents' || k === 'multiplier' ? Number(v) : v;
        btn.parentElement!.querySelectorAll('[role="radio"]').forEach((o) => o.setAttribute('aria-checked', String(o === btn)));
        const ex = root!.querySelector<HTMLElement>('#ex')!;
        ex.innerHTML = example();
        ex.classList.remove('flash'); void ex.offsetWidth; ex.classList.add('flash');
      });
    });
    if (banksSection) wireBanks(opts);
    root!.querySelector('#go')!.addEventListener('click', () => saveChoice(opts.manage ? 'manage' : 'enroll'));
    root!.querySelector('#rec')!.addEventListener('click', () => {
      if (opts.manage) { choice.paused = !session!.rules.paused; saveChoice('manage'); }
      else { choice = {}; bank(); }
    });
  }

  /**
   * Load and wire the funding-account list. Switching and removing hit the API and re-render in
   * place; adding runs the same link flow as enrollment, which attaches another account to the
   * consumer's existing PSP user.
   */
  function wireBanks(opts: { manage?: boolean }) {
    const host = () => root!.querySelector<HTMLElement>('#accts');

    const paint = () => {
      const el = host();
      if (!el) return;
      el.innerHTML = banksBody();
      const add = root!.querySelector('#addbank');
      if (add) add.textContent = addLabel();
      el.querySelectorAll<HTMLButtonElement>('[data-use]').forEach((b) =>
        b.addEventListener('click', () => act(() => api.call('/api/link/bank', { bankAccountId: b.dataset.use }, 'PUT'))));
      el.querySelectorAll<HTMLButtonElement>('[data-rm]').forEach((b) =>
        b.addEventListener('click', () => act(() => api.call(`/api/link/bank/${encodeURIComponent(b.dataset.rm!)}`, undefined, 'DELETE'))));
    };

    const act = async (run: () => Promise<unknown>) => {
      const el = host();
      if (el) el.style.opacity = '.5';
      try {
        const r = await run() as { profile?: Bank & { bankLogoUrl?: string | null } };
        if (r?.profile) {
          session!.bank = r.profile;
          session!.bankLinked = r.profile.status !== 'NO_BANK';
        }
        await load();
        emit({ type: 'bank_linked', status: String(session!.bank?.status ?? 'NO_BANK') });
      } catch (err) { fail(err as Error); return; }
      if (el) el.style.opacity = '';
      paint();
    };

    // Linking runs the normal flow and ends on the success screen; drop the cache so the list
    // is rebuilt from the PSP next time settings open.
    root!.querySelector('#addbank')!.addEventListener('click', () => { accountsCache = []; banksState = 'loading'; bank(); });

    const load = async () => {
      try {
        const r = await api.call('/api/link/banks') as { accounts: FundingAccount[]; stale?: boolean };
        accountsCache = r.accounts;
        banksState = r.stale ? 'stale' : 'ok';
      } catch { accountsCache = []; banksState = 'error'; }
    };

    if (banksState === 'loading' || !accountsCache.length) load().then(paint);
    else paint();
    void opts;
  }

  async function saveChoice(mode: 'enroll' | 'manage') {
    const btn = root!.querySelector<HTMLButtonElement>('#go');
    if (btn) btn.disabled = true;
    try {
      if (Object.keys(choice).length) {
        const r: any = await api.call('/api/link/settings', choice, 'PUT');
        if (r.rules) session!.rules = r.rules;
        if (r.accrual) session!.accrual = r.accrual;
        emit({ type: 'settings_saved', settings: choice });
      }
      if (mode === 'manage') { success(session!.bank, { managed: true }); return; }
      session!.bankLinked ? success(session!.bank) : bank();
    } catch (err) {
      showErr(friendly(err as Error));
      if (btn) btn.disabled = false;
    }
  }

  /** Finish any hosted flow (Stripe setupIntentId / Aerosync connectionId) and land on success/pending. */
  async function completeLink(payload: Record<string, string>) {
    show('bank', `${lockup()}<h1 id="sb-title">Confirming…</h1><p class="sub">Recording your authorization.</p><div class="spin"></div>`);
    const done: any = await api.call('/api/link/bank-link/complete', {
      payload,
      mandate: { userAgent: navigator.userAgent, acceptedAt: agreedAt || new Date().toISOString() },
    });
    if (!root) return; // closed while confirming
    emit({ type: 'bank_linked', status: done.profile.status });
    const bankInfo: Bank = { bankName: done.profile.bankName, bankLast4: done.profile.bankLast4, status: done.profile.status, bankLogoUrl: done.profile.bankLogoUrl };
    if (done.profile.status === 'VERIFIED') success(bankInfo);
    else pending(bankInfo);
  }

  async function bank(details?: Record<string, string>) {
    if (needsConsent()) { consent(); return; }
    if (linking) return; // a second Enter or click while the first link is in flight must not start another
    linking = true;
    show('bank', `${lockup()}<h1 id="sb-title">Connecting to ${esc(pspName())}…</h1><p class="sub">Choose your bank and sign in securely.</p><div class="spin"></div>`);
    emit({ type: 'bank_link_started' });
    try {
      const s: any = await api.call('/api/link/bank-link', details ? { consumer: details } : {});
      if (s.session.kind === 'preview') { success({ bankName: 'Preview Bank', bankLast4: '0000', status: 'VERIFIED' }); return; }
      if (s.session.kind === 'collect_details') { collectDetails(s.session.requiredFields || []); return; }
      if (s.session.kind === 'confirm_mfa') { mfa(s.session.mfaChannel === 'sms' ? 'sms' : 'email'); return; }
      if (s.session.kind === 'aerosync_widget') { await aerosync(s.session); return; }
      if (s.session.kind !== 'stripe_setup_intent') throw new Error(`Unsupported link flow: ${s.session.kind}`);
      const pk = s.publishableKey || session!.publishableKey;
      if (!pk) throw new Error('No publishable key configured for this merchant');
      const stripe = await loadStripe(pk);
      const c = session!.consumer;
      const holder = [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email;
      const collected = await stripe.collectBankAccountForSetup({
        clientSecret: s.session.clientSecret,
        params: { payment_method_type: 'us_bank_account', payment_method_data: { billing_details: { name: holder, email: c.email } } },
        expand: ['payment_method'],
      });
      if (collected.error) throw new Error(collected.error.message);
      if (collected.setupIntent.status === 'requires_payment_method') { consent(); return; } // user cancelled Stripe's modal
      const confirmed = await stripe.confirmUsBankAccountSetup(s.session.clientSecret);
      if (confirmed.error) throw new Error(confirmed.error.message);
      await completeLink({ setupIntentId: confirmed.setupIntent.id });
    } catch (err) {
      fail(err as Error);
    } finally {
      linking = false;
    }
  }

  /** Aeropay needs legal name + mobile before it can create the consumer. */
  function collectDetails(fields: string[]) {
    const c = session!.consumer;
    const need = (k: string) => fields.includes(k);
    show('details', `
      ${lockup()}
      <h1 id="sb-title">A little about you</h1>
      <p class="sub">${esc(pspName())} uses this to set up your bank transfers. Nothing is shared with ${name()}.</p>
      <form id="f" novalidate>
      ${need('firstName') || need('lastName') ? `<label class="lbl" for="fn">Legal name</label><input class="in" id="fn" name="given-name" placeholder="First name" aria-label="First name" autocomplete="given-name" value="${esc(c.firstName || '')}"><input class="in" id="ln" name="family-name" placeholder="Last name" aria-label="Last name" autocomplete="family-name" value="${esc(c.lastName || '')}">` : ''}
      ${need('phone') ? `<label class="lbl" for="ph">Mobile number</label><input class="in" id="ph" name="tel" type="tel" placeholder="+1 555 123 4567" inputmode="tel" autocomplete="tel">` : ''}
      <button class="b" id="go" type="submit">Continue</button>
      <div class="err" id="err" hidden></div>
      </form>`);
    onSubmit(() => {
      const v = (id: string) => root!.querySelector<HTMLInputElement>('#' + id)?.value.trim() || '';
      let phone = v('ph').replace(/[^\d+]/g, '');
      if (phone && !phone.startsWith('+')) phone = '+1' + phone.replace(/^1/, '');
      const details: Record<string, string> = {};
      if (v('fn')) details.firstName = v('fn');
      if (v('ln')) details.lastName = v('ln');
      if (phone) details.phone = phone;
      const labels: Record<string, string> = { firstName: 'first name', lastName: 'last name', phone: 'mobile number' };
      const ids: Record<string, string> = { firstName: 'fn', lastName: 'ln', phone: 'ph' };
      const missing = fields.filter((k) => !details[k]);
      if (missing.length) { showErr(`Add your ${missing.map((k) => labels[k] || k).join(' and ')} to continue.`, ids[missing[0]]); return; }
      if (details.phone && !/^\+[1-9]\d{6,14}$/.test(details.phone)) { showErr('Enter a mobile number with area code, like 415 555 0123.', 'ph'); return; }
      const btn = root!.querySelector<HTMLButtonElement>('#go');
      if (btn) { btn.disabled = true; btn.textContent = 'Continuing…'; }
      void bank(details);
    });
  }

  /** Aeropay sends a one-time code before it will let a bank be linked. */
  function mfa(channel: 'sms' | 'email' = 'email') {
    const sent = channel === 'sms' ? 'texted' : 'emailed';
    const where = channel === 'sms' ? 'your phone' : 'your inbox';
    show('mfa', `
      ${lockup()}
      <h1 id="sb-title">Enter the code we ${sent} you</h1>
      <p class="sub">${esc(pspName())} sent a 6-digit code to ${where} to confirm it's you.</p>
      <form id="f" novalidate>
      <label class="lbl" for="code">Verification code</label>
      <input class="in" id="code" name="one-time-code" inputmode="numeric" autocomplete="one-time-code" placeholder="6-digit code" maxlength="8" pattern="[0-9]*">
      <button class="b" id="go" type="submit">Verify</button>
      <div class="err" id="err" hidden></div>
      </form>`);
    onSubmit(async () => {
      const btn = root!.querySelector<HTMLButtonElement>('#go');
      const inp = root!.querySelector<HTMLInputElement>('#code');
      const code = inp?.value.trim() || '';
      if (!/^\d{4,8}$/.test(code)) { showErr('Enter the 6-digit code from the text message.', 'code'); return; }
      if (btn) { btn.disabled = true; btn.textContent = 'Verifying…'; }
      try {
        await api.call('/api/link/confirm-mfa', { code });
        void bank();
      } catch (err) {
        if (inp) inp.value = '';
        showErr(friendly(err as Error, channel), 'code');
        if (btn) { btn.disabled = false; btn.textContent = 'Verify'; }
      }
    });
  }

  /** Aerosync bank linking: real CDN widget, or the built-in mock page in an iframe. */
  async function aerosync(sess: { widgetUrl: string; token?: string; environment?: string; mock?: boolean }) {
    if (sess.mock) { await mockBank(sess); return; }
    // Real widget mounts by element id, so its host lives in the document, not our shadow root.
    const aero = await loadAerosync();
    let el = document.getElementById('sidebet-aerosync-host');
    if (!el) {
      el = document.createElement('div');
      el.id = 'sidebet-aerosync-host';
      document.body.appendChild(el);
    }
    show('bank', `${lockup()}<h1 id="sb-title">Opening your bank…</h1><p class="sub">Aerosync handles the sign-in — we never see your credentials.</p><div class="spin"></div>`);
    const cleanup = () => { if (el) el.innerHTML = ''; };
    const controls = aero.initWidget({
      token: sess.token,
      elementId: 'sidebet-aerosync-host',
      iframeTitle: 'Connect your bank',
      environment: sess.environment === 'production' ? 'production' : 'sandbox',
      onLoad: () => {},
      onEvent: () => {},
      onSuccess: (payload: any) => { cleanup(); void completeLink({ connectionId: String(payload?.connectionId || '') }).catch((err) => fail(err as Error)); },
      onClose: () => { cleanup(); consent(); },
      onError: (e: any) => { cleanup(); fail(new Error(e?.message || 'Bank link failed')); },
    });
    controls.launch();
  }


  /**
   * Sandbox bank picker, drawn in our own modal.
   *
   * The mock widget is also served as a page, but it is cross-origin: automation harnesses can type
   * into that frame and cannot click into it, which dead-ends every agent-driven run of the demo on
   * the bank step. Only the mock takes this path — the real Aerosync widget owns its own frame and
   * its own sign-in, and we never draw a bank's credential form ourselves.
   */
  async function mockBank(sess: { widgetUrl: string; token?: string }) {
    const base = sess.widgetUrl.replace(/\/widget$/, '');
    const boot = await fetch(`${base}/bootstrap?token=${encodeURIComponent(sess.token || '')}`);
    if (!boot.ok) throw new Error('The sandbox bank service did not respond.');
    const { connectionId, institutions } = await boot.json() as { connectionId: string; institutions: { name: string; color: string; logoUrl?: string | null }[] };
    const initials = (n: string) => {
      const w = n.split(/\s+/).filter((x) => !/^(of|the|and)$/i.test(x));
      return (w.length > 1 ? w[0][0] + w[1][0] : n.slice(0, 2)).toUpperCase();
    };
    const head = `${lockup()}<h1 id="sb-title">Link your bank</h1><p class="sub">Sandbox mode — every bank here is a demo; no real account is touched.</p><div class="mockbar"><b><img alt="" src="${esc(apiBase)}/sdk/aeropay-mark.png" decoding="async">aerosync</b><em>MOCK</em></div>`;

    const list = (q = '') => {
      const hits = institutions.filter((i) => i.name.toLowerCase().includes(q.toLowerCase()));
      show('bank', `${head}
        <label class="lbl" for="bq">Select your bank</label>
        <input class="in" id="bq" placeholder="Search institutions" autocomplete="off" value="${esc(q)}">
        <div class="banks">${hits.length ? hits.map((i) => `<button class="bank" data-n="${esc(i.name)}">${inst(i)}<span>${esc(i.name)}<small>${i.name === 'Aerobank' ? 'Test institution' : 'Checking · Savings'}</small></span></button>`).join('') : '<p class="sub">No institution matches that name.</p>'}</div>`);
      const q2 = root!.querySelector<HTMLInputElement>('#bq')!;
      q2.addEventListener('input', () => { const v = q2.value; list(v); const n = root!.querySelector<HTMLInputElement>('#bq')!; n.focus(); n.setSelectionRange(v.length, v.length); });
      root!.querySelectorAll<HTMLButtonElement>('.bank').forEach((b) => b.addEventListener('click', () => signIn(b.dataset.n!)));
    };

    /** An institution's tile: its logo on white where we have one, coloured initials otherwise. */
    const inst = (i: { name: string; color: string; logoUrl?: string | null }) => i.logoUrl
      ? `<i class="il"><img alt="" src="${esc(i.logoUrl)}" decoding="async" onerror="this.remove()"></i>`
      : `<i style="background:${esc(i.color)}">${esc(initials(i.name))}</i>`;
    const byName = (n: string) => institutions.find((i) => i.name === n) || { name: n, color: '#64748b' };

    const signIn = (bank: string) => {
      show('bank', `${head}
        <div class="mockh2">${inst(byName(bank))}<h2 class="mockh">${esc(bank)}</h2></div>
        <p class="sub">Sandbox sign-in. Any username and password is accepted; nothing is sent to ${esc(bank)}.</p>
        <form id="f" novalidate>
        <label class="lbl" for="u">Username</label><input class="in" id="u" autocomplete="off" value="demo">
        <label class="lbl" for="pw">Password</label><input class="in" id="pw" type="password" autocomplete="off" value="demo">
        <button class="b" id="go" type="submit">Sign in</button>
        <button class="link" id="back" type="button">Choose a different bank</button>
        </form>`);
      onSubmit(() => accounts(bank));
      root!.querySelector('#back')!.addEventListener('click', () => list());
    };

    /** Mirrors mockLast4() in src/mock/aeropay.ts — the sandbox must offer the account it creates. */
    const last4 = (name: string) => {
      let h = 0;
      for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 10000;
      return String(1000 + (h % 9000));
    };

    const accounts = (bank: string) => {
      show('bank', `${head}
        <div class="mockh2">${inst(byName(bank))}<h2 class="mockh">${esc(bank)}</h2></div>
        <p class="sub">Choose the account to connect for round-ups.</p>
        <button class="bank acct" id="chk"><span>${esc(bank)} Checking ••${last4(bank)}<small>$2,418.20</small></span></button>
        <button class="bank acct" disabled><span>${esc(bank)} Savings ••8890<small>Not eligible for round-ups</small></span></button>`);
      root!.querySelector('#chk')!.addEventListener('click', async () => {
        show('bank', `${head}<div class="spin"></div><p class="sub">Connecting ${esc(bank)}…</p>`, { noClose: true });
        try {
          await fetch(`${base}/connections/${encodeURIComponent(connectionId)}/institution?name=${encodeURIComponent(bank)}`, { method: 'POST' });
          await completeLink({ connectionId });
        } catch (err) { fail(err as Error); }
      });
    };

    list();
  }

  /**
   * The institution tile. Aerosync gives us a logo for the big banks and nothing for the rest,
   * so a missing logo is normal and falls back to the generic mark rather than a broken image.
   */
  function bankTile(bank: Bank): string {
    return bank.bankLogoUrl
      ? `<i class="bl"><img alt="" src="${esc(bank.bankLogoUrl)}" loading="eager" decoding="async" onerror="this.remove()"></i>`
      : '<i>\u{1F3E6}</i>';
  }

  function pending(bank: Bank) {
    show('pending', `
      ${lockup()}
      <h1 id="sb-title">Almost there</h1>
      <p class="sub">Your bank needs a quick verification. Stripe emailed you instructions — it usually takes 1–2 business days. Round-ups start tracking right away.</p>
      <div class="row">${bankTile(bank)}<div><b>${esc(bank.bankName || 'Bank')} ••${esc(bank.bankLast4 || '')}</b><span>${esc(String(bank.status).toLowerCase().replace('_', ' '))}</span></div></div>
      <button class="b" id="done">Done</button>`);
    root!.querySelector('#done')!.addEventListener('click', () => exit('closed'));
  }

  async function success(bank: Bank | null, opts: { managed?: boolean } = {}) {
    let st: StatusResult | null = null;
    if (!session?.preview) { try { st = await api.call<StatusResult>('/api/link/status'); } catch { /* fine */ } }
    if (!root) return; // closed while we were fetching
    const a = st?.accrual || session!.accrual;
    const r = session!.rules;
    const last = st?.debits?.find((d) => d.status === 'SETTLED');
    const title = opts.managed ? (r.paused ? 'Round-ups paused' : 'Saved') : r.paused ? 'Round-ups are paused' : 'Round-ups are on';
    const sub = r.paused
      ? `Nothing will accrue until you resume. ${bank ? `${esc(bank.bankName || 'Your bank')} ••${esc(bank.bankLast4 || '')} stays linked.` : ''}`
      : bank ? `${esc(bank.bankName || 'Your bank')} ••${esc(bank.bankLast4 || '')} is linked to ${name()}.` : `Linked to ${name()}.`;
    show('success', `
      <div class="ok">${r.paused ? '❚❚' : '✓'}</div>
      <h1 id="sb-title">${title}</h1>
      <p class="sub">${sub}</p>
      <div class="kv"><span>Status</span><b><span class="pill ${r.paused ? 'off' : 'on'}">${r.paused ? 'Paused' : 'Active'}</span></b></div>
      <div class="kv" style="margin-top:8px"><span>Accrued so far</span><b id="acc">${money(a.accruedCents)} / ${money(a.thresholdCents)}</b></div>
      <div class="bar"><div id="fill" style="width:0%"></div></div>
      <div class="kv"><span>Rule</span><b>${r.strategy === 'CENTS' ? 'Just the cents' : 'Up to the next dollar'}${r.multiplier > 1 ? ` · ${r.multiplier}×` : ''}</b></div>
      ${last ? `<div class="kv" style="margin-top:6px"><span>Last transfer</span><b>${money(last.amountCents)} · ${when(last.updatedAt || last.createdAt)}</b></div>` : ''}
      <button class="b" id="done">Done</button>
      ${hasChoices() ? '<button class="lnk" id="manage">Manage round-ups</button>' : ''}`);
    // Bar fills in from zero so the progress reads as motion, not a static line.
    requestAnimationFrame(() => { const f = root?.querySelector<HTMLElement>('#fill'); if (f) f.style.width = `${Math.round(a.pct * 100)}%`; });
    root!.querySelector('#done')!.addEventListener('click', () => {
      emit({ type: 'success' });
      config.onSuccess?.({ bank, accrual: a, rules: r, wallet: st?.wallet || session!.wallet });
      close();
    });
    root!.querySelector('#manage')?.addEventListener('click', () => { choice = {}; choose({ manage: true }); });
  }

  function fail(err: Error) {
    emit({ type: 'error', message: err.message });
    const expired = /expired|link_token/.test(String((err as any).code || err.message));
    show('error', `<h1 id="sb-title">${expired ? 'Session expired' : 'Something went wrong'}</h1><p class="sub">${esc(expired ? 'This link session timed out. Close this and start again from the app.' : friendly(err))}</p>${expired ? '' : '<button class="b" id="retry">Try again</button>'}<button class="lnk" id="cancel">Close</button>`);
    root!.querySelector('#retry')?.addEventListener('click', () => bank());
    root!.querySelector('#cancel')!.addEventListener('click', () => exit(expired ? 'expired' : 'error', err));
  }

  function exit(reason: 'closed' | 'error' | 'expired', err?: Error) {
    emit({ type: 'exit', reason });
    config.onExit?.(reason, err);
    close();
  }

  function close() {
    document.removeEventListener('keydown', onDocKey);
    if (msgListener) { window.removeEventListener('message', msgListener); msgListener = null; }
    if (prevOverflow !== null) { document.documentElement.style.overflow = prevOverflow; prevOverflow = null; }
    linking = false;
    if (host) { setBackgroundInert(host, false); host.remove(); host = null; root = null; }
    if (openInstance === instanceId) openInstance = null;
    (lastFocus as HTMLElement | null)?.focus?.();
  }

  async function open() {
    if (openInstance) return; // a dialog is already up; never stack a second one
    openInstance = instanceId;
    mount();
    emit({ type: 'open' });
    choice = {};
    show('loading', `<h1 id="sb-title">Loading…</h1><div class="spin"></div>`, { noClose: true });
    try {
      session = await api.call<Session>('/api/link/session' + (config.previewDraft ? '?draft=1' : ''));
      theme = buildTheme();
      // settings()/startAt shortcuts only apply to enrolled consumers; a first-timer always starts at consent.
      if (config.startAt === 'choose') { session.bankLinked ? choose({ manage: true }) : consent(); return; }
      if (config.startAt === 'bank') { bank(); return; }
      if (session.bankLinked) { success(session.bank); return; }
      if (session.bank && session.bank.status === 'PENDING_VERIFICATION') { pending(session.bank); return; }
      if (config.explainer === false) { hasChoices() ? choose() : bank(); return; }
      consent();
    } catch (err) {
      theme = buildTheme();
      fail(err as Error);
    }
  }

  return { open, close, destroy: close };
}

/** Open straight to "Manage round-ups" (threshold / rule / pause) for an already-enrolled consumer. */
export function settings(config: LinkConfig): LinkHandler {
  return create({ ...config, startAt: 'choose' });
}

/** Tiny progress widget: renders "accrued / threshold" with a bar into `el`, auto-refreshing. */
export function widget(opts: { linkToken: string; el: HTMLElement | string; apiBase?: string; refreshMs?: number; theme?: Partial<Theme>; onManage?: () => void }): { refresh(): Promise<void>; destroy(): void } {
  const el = typeof opts.el === 'string' ? document.querySelector<HTMLElement>(opts.el) : opts.el;
  if (!el) throw new Error('widget: element not found');
  // Pick text colors from the container's actual background so the widget reads on light or dark bars.
  const bg = getComputedStyle(el).backgroundColor;
  const m = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/.exec(bg);
  const containerDark = m && (m[4] === undefined || Number(m[4]) > 0.3) ? (0.2126 * +m[1] + 0.7152 * +m[2] + 0.0722 * +m[3]) < 128 : window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  const theme: Theme = { ...LIGHT, ...(containerDark ? { ...DARK, well: 'rgba(255,255,255,.14)', accent: '#FFFFFF', accentInk: '#1A1F36' } : {}), ...(opts.theme || {}) };
  const api = new Api(opts.apiBase || DEFAULT_API, opts.linkToken);
  const root: ShadowRoot | HTMLElement = el.attachShadow ? el.shadowRoot || el.attachShadow({ mode: 'open' }) : el;
  const q = <T extends HTMLElement>(sel: string) => (root as ParentNode).querySelector<T>(sel);
  const ok = '#0E9F6E';
  // Built once; refreshes patch text and widths so the bar and figures can animate between polls.
  root.innerHTML = `<style>
    :host{all:initial;display:block}
    .w{font-family:${theme.font};color:${theme.text};font-variant-numeric:tabular-nums;position:relative}
    .r{display:flex;justify-content:space-between;align-items:baseline;font-size:13px;color:${theme.muted}}
    .r b{color:${theme.text};font-size:14px}
    .bar{height:8px;border-radius:999px;background:${theme.well};overflow:hidden;margin-top:6px}
    .bar div{height:100%;width:0;background:${theme.accent};transition:width .5s cubic-bezier(.2,.8,.2,1)}
    .bar.full div{background:${ok}}
    .n{font-size:12px;color:${theme.muted};margin-top:6px;display:flex;justify-content:space-between;gap:12px;min-height:16px}
    .n a{color:${theme.text};cursor:pointer;text-decoration:underline;white-space:nowrap;padding:6px 0;margin:-6px 0}
    /* The gear carries the brand's accent so it reads as the way in, not decoration. */
    .n .gear{flex:none;width:28px;height:28px;margin:-6px -4px -6px 0;padding:0;border:0;cursor:pointer;
      border-radius:8px;background:${theme.accent};color:${theme.accentInk};display:flex;align-items:center;
      justify-content:center;transition:filter .12s,transform .12s}
    .n .gear:hover{filter:brightness(1.12);transform:rotate(20deg)}
    .n .gear:focus-visible{outline:2px solid ${theme.accent};outline-offset:2px}
    .n .gear svg{display:block;width:16px;height:16px}
    @media (prefers-reduced-motion:reduce){.n .gear{transition:none}.n .gear:hover{transform:none}}
    .n img{height:11px;width:auto;vertical-align:-1px;background:#fff;border-radius:2px;padding:1px 2px;margin-right:3px}
    .t{font-size:12px;color:${theme.muted};margin-top:4px;display:none}
    .t.show{display:block}
    .t.hot{color:${ok};font-weight:600}
    .err{font-size:12px;color:#B42318;margin-top:4px;display:none}
    .c{position:absolute;top:0;left:50%;width:6px;height:10px;border-radius:2px;pointer-events:none;animation:fall 1.1s ease-out forwards}
    @keyframes fall{0%{transform:translate(0,0) rotate(0);opacity:1}100%{transform:translate(var(--dx),var(--dy)) rotate(var(--r));opacity:0}}
    @media (prefers-reduced-motion:reduce){.bar div{transition:none}.c{display:none}}
  </style>
  <div class="w" aria-live="polite">
    <div class="r"><span id="lbl">Round-ups</span><b><span id="acc">$0.00</span> / <span id="thr">$0.00</span></b></div>
    <div class="bar" id="bar"><div id="fill"></div></div>
    <div class="n"><span id="sub"></span>${opts.onManage ? `<button id="mg" class="gear" aria-label="Round-up settings" title="Round-up settings">${GEAR}</button>` : ''}</div>
    <div class="t" id="tx"></div>
    <div class="err" id="err"></div>
  </div>`;
  const mg = q<HTMLAnchorElement>('#mg');
  mg?.addEventListener('click', () => opts.onManage?.());
  mg?.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); opts.onManage?.(); } });

  let prevAccrued: number | null = null;
  let knownDebits: Set<string> | null = null;
  let hotTimer: ReturnType<typeof setTimeout> | null = null;

  function confetti() {
    if (reducedMotion()) return;
    const w = q<HTMLElement>('.w'); if (!w) return;
    const colors = [theme.accent, ok, '#F5B700', '#3B82F6', '#EC4899'];
    for (let i = 0; i < 26; i++) {
      const p = document.createElement('i');
      p.className = 'c';
      const ang = (Math.random() * 160 + 10) * Math.PI / 180;
      const dist = 60 + Math.random() * 120;
      p.style.cssText = `background:${colors[i % colors.length]};--dx:${Math.cos(ang) * dist * (Math.random() > .5 ? 1 : -1)}px;--dy:${-Math.sin(ang) * dist + 80}px;--r:${Math.round(Math.random() * 720 - 360)}deg;animation-delay:${Math.random() * .12}s`;
      w.appendChild(p);
      setTimeout(() => p.remove(), 1400);
    }
  }

  /** The one moment money actually moves: fill to 100% in green, show the receipt line, then settle back to the empty bar. */
  function transferMoment(d: { id: string; amountCents: number; updatedAt?: string; createdAt?: string }, first: boolean, after: () => void) {
    const bar = q<HTMLElement>('#bar')!, fill = q<HTMLElement>('#fill')!, tx = q<HTMLElement>('#tx')!;
    bar.classList.add('full'); fill.style.width = '100%';
    tx.className = 't show hot';
    tx.textContent = `↑ ${money(d.amountCents)} moved to your wallet${first ? ' — your first transfer' : ''}`;
    if (first) confetti();
    if (hotTimer) clearTimeout(hotTimer);
    hotTimer = setTimeout(() => { bar.classList.remove('full'); after(); tx.className = 't show'; }, reducedMotion() ? 0 : 1600);
  }

  async function refresh() {
    try {
      const s = await api.call<StatusResult>('/api/link/status');
      const a = s.accrual;
      q<HTMLElement>('#err')!.style.display = 'none';
      const acc = q<HTMLElement>('#acc')!, thr = q<HTMLElement>('#thr')!, fill = q<HTMLElement>('#fill')!, sub = q<HTMLElement>('#sub')!, tx = q<HTMLElement>('#tx')!;
      thr.textContent = money(a.thresholdCents);
      const settled = (s.debits || []).filter((d) => d.status === 'SETTLED');
      const latest = settled[0];
      const isNew = knownDebits !== null && latest && !knownDebits.has(latest.id);
      knownDebits = new Set(settled.map((d) => d.id));

      const settle = () => {
        countUp(acc, prevAccrued ?? a.accruedCents, a.accruedCents);
        fill.style.width = `${Math.round(a.pct * 100)}%`;
        prevAccrued = a.accruedCents;
      };
      // Name the account. Without it the sheet never confirms *which* bank is attached — that
      // only appeared inside the modal, which the user has already closed. The institution mark
      // rides along when the API resolved one; a bank we hold no artwork for just shows its name.
      // What has actually left the bank. When the merchant holds the money (MERCHANT_WALLET) the
      // consumer wallet stays at zero by design, so reading it here printed "$0.00 transferred"
      // beside "Last transfer $5.13". Settled debits are the truth in both custody models.
      const moved = (s as any).rules?.destination === 'MERCHANT_WALLET'
        ? settled.reduce((n, d) => n + (d.amountCents || 0), 0)
        : s.wallet.balanceCents;
      const mark = s.bank?.bankLogoUrl
        ? `<img alt="" src="${esc(s.bank.bankLogoUrl)}" decoding="async" onerror="this.remove()">`
        : '';
      sub.innerHTML = s.bankLinked
        ? esc(a.remainingCents > 0 ? `${money(a.remainingCents)} to go` : 'Transfer on its way')
          + (s.bank?.bankName
            ? ` · from ${mark}${esc(`${s.bank.bankName}${s.bank.bankLast4 ? ` ••${s.bank.bankLast4}` : ''}`)}`
            : '')
          + esc(` · ${money(moved)} transferred`)
        : 'Link a bank to start rounding up';
      if (mg) {
        const label = s.bankLinked ? 'Round-up settings' : 'Set up round-ups';
        mg.setAttribute('aria-label', label);
        mg.setAttribute('title', label);
      }

      if (isNew && latest) {
        let first = settled.length === 1;
        try { const k = 'sb-link:celebrated:' + latest.id; if (localStorage.getItem(k)) first = false; else localStorage.setItem(k, '1'); } catch { /* private mode */ }
        transferMoment(latest, first, settle);
        return;
      }
      settle();
      if (latest) { tx.className = 't show'; tx.textContent = `Last transfer ${money(latest.amountCents)} · ${when(latest.updatedAt || latest.createdAt)}`; }
      else tx.className = 't';
    } catch (err) {
      const e = q<HTMLElement>('#err')!; e.style.display = 'block'; e.textContent = friendly(err as Error);
    }
  }
  const timer = opts.refreshMs === 0 ? null : setInterval(refresh, opts.refreshMs || 30_000);
  void refresh();
  return { refresh, destroy: () => { if (timer) clearInterval(timer); if (hotTimer) clearTimeout(hotTimer); root.innerHTML = ''; } };
}

export const version: string = typeof __SDK_VERSION__ !== 'undefined' ? __SDK_VERSION__ : 'dev';

export default { create, settings, widget, status, version };
